<template>
  <div
    id="product-form"
    class="product-form"
    data-sign-post-location="main"
    :data-sticky-add-to-cart-feature="!variant || variant.available || showBackInStock"
    js-sign-post
    js-product-form="container"
  >
    <form
      class="product-form__form"
      @submit.prevent="handleAddToCart"
    >
      <div
        v-if="showVariants && !product.hasOnlyDefaultVariant"
        class="product-form__variants"
      >
        <div class="product-form__content">
          <div class="product-form__content-text">
            <p
              v-if="variantSelectText"
              class="text-body-xs-bold text-body-s-bold-desktop"
              v-text="variantSelectText"
            />

            <span
              v-if="lowInStockValue"
              class="
                product-form__low-in-stock
                text-body-xs-bold
                text-body-s-bold-desktop
              "
              v-text="$string(
                'product.form.low_in_stock',
                { replace: { num: lowInStockValue} }
              )"
            />

            <button
              v-if="displaySizeGuideLink"
              class="product-form__size-guide-cta product_test_b"
              type="button"
              @click="openSizeGuideModal">

              <span v-text="$string('size_finder.size_finder_cta')"></span>

              <span class="product-form__size-guide-inner">
                <ul class="product-form__size-guide-images">
                  <li v-for="(imageItem, index) in sizeGuideMedia"
                    :key="`size-${product.title}-${index}`">
                    <img
                      class="product-form__size-guide-image"
                      :alt="imageItem.alt"
                      :src="imageItem.image?.src || mediaItem.src"
                    >
                  </li>
                </ul>

                <span class="visually-hidden" v-text="$string('accessibility.carousel.next')"/>
                <span><icon-arrow-right /></span>
              </span>
            </button>
          </div>

          <transition-height>
            <p
              v-if="state === 'empty'"
              class="product-form__empty-error text-body-xs-regular"
              v-html="variantSelectError"
            />
          </transition-height>
        </div>

        <div class="product-form__variant-selector">
          <selection-tab
            v-for="(variantObject, index) of productVariants"
            :id="`variant-${variantObject.id}`"
            :key="`variant-${index}-${variantObject.id}`"
            v-model="currentVariantId"
            class="product-form__variant"
            :disabled="!variantObject.available"
            :label="getVariantTitle(variantObject)"
            :loading="loading"
            name="id"
            :value="variantObject.id"
          />
        </div>
      </div>

      <div
        v-if="sizeExplainer"
        class="product-form__size-explainer"
      >
        <p
          class="
          text-body-xs-regular
          text-body-s-regular-desktop
        "
          v-text="sizeExplainer"
        />
      </div>

      <div class="product-form__message">
        <div
          v-if="messageText && defaultMessage.icon"
          class="product-form__message-icon"
          v-html="defaultMessage.icon"
        />

        <p
          v-if="messageText"
          class="
          product-form__message-text
          text-body-xs-regular
          text-body-s-regular-desktop
        "
          v-text="messageText"
        />
      </div>

      <quantity-selector
        v-if="quantitySelector"
        class="product-form__quantity-selector"
        large
        :min="1"
        :quantity="quantity"
        :state="state"
        @update-quantity="handleQuantityUpdate"
      />

      <input
        v-else
        name="quantity"
        readonly
        type="hidden"
        :value="quantity"
      >

      <div class="product-form__button-container">
        <div
          class="product-form__add-to-cart-container"
          :class="{ 'product-form__add-to-cart--sticky--half': isSticky && abTest }"
          js-product-form="addToCartContainer"
        >
          <div
            v-if="abTest"
            :class="{ 'is-hidden': !isSticky }"
            class="product-form__add-to-cart-info"
          >
            <product-prices
              class="main-product__prices-container"
              class-price="text-body-m-bold text-heading-l-desktop"
              :available="variant.available"
              :compare-at-price="variant.compareAtPrice?.amount"
              :price="variant.price?.amount || liquid.selectedOrFirstAvailableVariant?.price?.amount"
              :varies="variant.price?.varies || liquid.selectedOrFirstAvailableVariant?.price?.varies"
            />

            <div
              class="product-form__add-to-cart-reviews"
              js-yotpo-star-rating="reviews"
              @click="scrollToReviews"
            >
              <div
                class="product-form__star-icon is-hidden"
                ref="starIcon"
              >
                <icon-star />
              </div>
            </div>
          </div>

          <btn
            v-if="variant && !variant.available && showBackInStock"
            id="BIS_trigger"
            class="product-form__add-to-cart product-form__back-in-stock-btn"
            :label="$string('product.cta.notify')"
            :data-variant-id="variant.id"
          />

          <btn
            v-else
            class="product-form__add-to-cart"
            :class="{ [$class('disabled')]: variant && !variant.available }"
            :label="addToCartText"
            type="submit"
            js-product-form="addToCart"
          />
        </div>
      </div>
    </form>

    <notification-panel
      v-if="error"
      class-container="product-form__error"
      :show-close="false"
      :text="error"
      type="error"
    />
  </div>
</template>

<script>

/**
 * Vue: Product form (product-form)
 * -----------------------------------------------------------------------------
 * Product form to handle variant selection and adding to cart.
 *
 * @param {Boolean} [loading] - Loading state.
 * @param {Number} [lowInStock] - Amount to display low in stock warning message
 * @param {string} sizeExplainer - Product form size explainer message.
 * @param {Object} defaultMessage - Product form default message.
 * @param {Object} shipping - Variant shipping estimate.
 * @param {Object} backInStock - Variant back in stock message.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Boolean} [quantitySelector] - Show quantity selector.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 * @param {Object} relatedCollection - Object containing collection handle and
 * canvas formatted color related products.
 *
 * @emits updateVariant - Emit new variant object.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'

import NotificationPanel from '~async/utils/notification-panel/notification-panel'
import TransitionHeight from '~async/transition-height/transition-height'
import QuantitySelector from '~async/quantity-selector/quantity-selector'
import SelectionTab from '~async/utils/selection-tab/selection-tab'
import IconArrowRight from '~icons/directional-navigation/arrow/right.svg'
import IconStar from '~icons/general/star.svg'
import ProductPrices from '~global/product-prices/product-prices'
import { scrollToElement } from '~/helpers/general'

export default {
  name: 'ProductForm',

  components: {
    Btn,
    NotificationPanel,
    TransitionHeight,
    QuantitySelector,
    SelectionTab,
    IconArrowRight,
    IconStar,
    ProductPrices,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    lowInStock: {
      type: Number,
      default: 0,
    },
    sizeExplainer: {
      type: String,
      default: () => '',
    },
    liquid: {
      type: Object,
      default: () => ({
        description: '',
        price: 0,
        title: '',
      }),
    },
    defaultMessage: {
      type: Object,
      default: () => ({
        icon: '',
      }),
    },
    backInStock: {
      type: Object,
      default: () => ({
        enabled: true,
      }),
    },
    shipping: {
      type: Object,
      default: () => ({
        enabled: true,
        estimate: '',
        range: '',
      }),
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    quantitySelector: {
      type: Boolean,
      default: true,
    },
    isSticky: {
      type: Boolean,
      default: false,
    },
    abTest: {
      type: Boolean,
      default: false,
    },
    relatedCollection: {
      type: Object,
      default: () => ({
        handle: '',
        products: [],
      }),
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  emits: {
    'update-variant': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      addedVariant: false,
      currentVariantId: this.variant?.id || 0,
      error: '',
      lineItemProperties: {},
      quantity: 1,
      state: 'ready',
      variantSelector: '',
      timeout: {},
      displaySizeGuideLink: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      const variant = this.getVariantFromId(this.currentVariantId)

      if (variant && !variant.available) {
        return this.$string('product.cta.out_of_stock')
      }

      return this.state === 'added'
        ? this.$string('product.cta.added_to_cart')
        : this.$string('product.cta.add_to_cart')
    },

    /**
     * Compute product variants to iterate over.
     * @returns {Array}
     */
    productVariants() {
      if (this.loading) {
        return [false, false, false, false, false]
      }

      return this.product.variants
    },

    /**
     * Check on what image should show
     * based on the localStorage gender
     * by default it should show the 'FEMALE'
     * @returns {String}
     */
    sizeGuideMedia() {
      const maleMediaItems = this.product.media.filter(item => item.alt.includes('*MALE*'));
      const activeGender = localStorage.getItem('activeGender');

      if (activeGender === 'male' && maleMediaItems.length > 0) {
        return maleMediaItems.slice(0, 2);
      }

      return this.product.media.slice(0, 2);
    },

    /**
     * Compute if product variants contain size or amount and show selector.
     * @returns {Boolean}
     */
    showVariants() {
      return this.product.giftCard || this.product.variants?.some(
        variant => variant.selectedOptions.some(
          option => option.name.toLowerCase() === 'size',
        ),
      )
    },

    /**
     * Compute variant select text.
     * - If no variant is selected, show default select message
     * - If variant is selected, show updated message with size
     * @returns {String | Boolean}
     */
    variantSelectText() {
      const variant = this.getVariantFromId(this.currentVariantId)

      const sizeVariant = variant?.selectedOptions?.find(option => option.name.toLowerCase() === 'size')
      const sizeValue = sizeVariant?.value?.toLowerCase()
      const sizeString = sizeValue ? this.$string(`product.form.size_${sizeValue}`) : ''
      const sizeText = sizeString === 'No string found' ? sizeVariant?.value : sizeString

      switch (this.variantSelector) {
        case 'size':
          if (!sizeVariant) {
            return this.$string('product.form.select_size')
          }

          return this.$string('product.form.size_preview', {
            replace: { size: sizeText },
          })

        case 'amount':
          return this.$string('product.form.select_amount')

        default:
          return false
      }
    },

    /**
     * Compute variant select text.
     * @returns {String | Boolean}
     */
    variantSelectError() {
      switch (this.variantSelector) {
        case 'size':
          return this.$string('product.form.size_error')

        case 'amount':
          return this.$string('product.form.amount_error')

        default:
          return false
      }
    },

    /**
     * Compute display of low in stock message.
     * @returns {Boolean | Number}
     */
    lowInStockValue() {
      const variant = this.getVariantFromId(this.currentVariantId)
      const stockAmount = Number(this.lowInStock)

      if (
        !variant ||
        stockAmount <= 0 ||
        variant?.inventory <= 0 ||
        variant?.inventory > stockAmount
      ) {
        return false
      }

      return Number(variant.inventory)
    },

    /**
     * Compute product form message.
     * @returns {String}
     */
    messageText() {
      const variant = this.getVariantFromId(this.currentVariantId)

      if (variant) {
        if (variant.available && this.shipping?.enabled) {
          const shippingEstimate =
            variant.shippingEstimate
              ? Number(variant.shippingEstimate)
              : Number(this.shipping?.estimate)

          const start = new Date()
          start.setDate(start.getDate() + shippingEstimate)

          const end = new Date()
          end.setDate(end.getDate() + shippingEstimate + Number(this.shipping.range))

          const firstDate = new Date(start).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
          })

          const lastDate = new Date(end).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
          })

          return this.$string('product.form.estimated_delivery', {
            replace: {
              first_date: firstDate,
              last_date: lastDate,
            },
          })
        }

        if (!variant.available && variant.restockDate) {
          return this.$string('product.form.restock_text', {
            replace: {
              date: variant.restockDate,
            },
          })
        }
      }

      return this.$string('product.form.default_message')
    },

    /**
     * Compute back in stock button display.
     * @returns {Boolean}
     */
    showBackInStock() {
      const productTags = this.product.tags

      if (
        !this.backInStock.enabled ||
        productTags.includes('LTD_OLD') ||
        productTags.includes('disable back in stock') ||
        productTags.includes('injection')
      ) {
        return false
      }

      return true
    },
  },

  watch: {

    /**
     * Watch currentVariantId to update Canvas variant when select changes.
     */
    currentVariantId() {
      if (this.variant.id === this.currentVariantId) {
        return
      }

      const variant = this.getVariantFromId(this.currentVariantId)

      if (!variant) {
        return
      }

      this.storeSelectedSize(variant)
      this.$emit('update-variant', variant)
      this.state = 'ready'
    },

    /**
     * Runs observe and clone if ABtest value changes
     */
    abTest() {
      if (this.abTest) {
        setTimeout(() => {
          this.observeAndClone('yotpo-summary-section', '[js-yotpo-star-rating="reviews"]');
        }, this.$timing('normal'))
      }

    },

    /**
     * Watch product to update selected variant when product data is loaded.
     */
    product() {
      this.getCurrentVariant(true)
      this.quantity = 1
    },
  },

  mounted() {
    this.checkSizeGuidePresence()
    this.getCurrentVariant(true)
    this.getVariantSelector()

    if (this.abTest) {
      this.observeAndClone('yotpo-summary-section', '[js-yotpo-star-rating="reviews"]');
    }
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
    }),

    /**
     * Scrolls to main reviews if it exists
     */
    scrollToReviews() {
      const mainReviews = document.querySelector('.yotpo-bottom-line-summary')

      if (mainReviews) {
        scrollToElement(mainReviews)
      }
    },

    /**
     * Finds yotpo widgets and clones data for sticky ATC
     * @param {Object} targetClassName - class name of yotpo widget
     * @param {Object} containerSelector - selector where we want to move it to.
     */
     observeAndClone(targetClassName, containerSelector) {
      const container = document.querySelector(containerSelector);

      if (!container) {
        return;
      }

      const observer = new MutationObserver((mutationsList, obs) => {
        mutationsList.forEach((mutation) => {
          if (mutation.type === 'childList') {

            const targetElement = document.querySelector(`.${targetClassName}`)
            const bottomLineScoreElement = document.querySelector('.yotpo-bottom-line-score')
            const reviewsCountText = document.querySelector('.yotpo-bottom-line-basic-text')
            const alreadyCloned = container.classList.contains('reviews__clone')

            if (!alreadyCloned) {

              if (targetElement && bottomLineScoreElement && reviewsCountText) {
                container.classList.add('reviews__clone')
                const clonedBottomLineScore = bottomLineScoreElement.cloneNode(true)

                const reviewsCount = document.createElement('div');
                reviewsCount.classList.add('product-form__reviews-count');
                reviewsCount.textContent = reviewsCountText.innerHTML.replace('Based on', '').trim();

                container.appendChild(clonedBottomLineScore)
                container.appendChild(reviewsCount)

                if (this.$refs?.starIcon) {
                  this.$refs.starIcon.classList.remove('is-hidden')
                }

                obs.disconnect()
              }
            }
          }
        });
      });

      observer.observe(document.body, { childList: true, subtree: true });
    },

    checkSizeGuidePresence() {
      if (this.getComponentExists('size-finder')) {
        this.displaySizeGuideLink = true
      }
    },

    openSizeGuideModal() {
      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'sizeFinderModal',
      })
    },

    getVariantSelector() {
      if (this.product.giftCard) {
        this.variantSelector = 'amount'
        return
      }

      const hasSizeVariant = this.product.variants?.some(
        variant => variant.selectedOptions.some(
          option => option.name.toLowerCase() === 'size',
        ),
      )

      if (hasSizeVariant) {
        this.variantSelector = 'size'
        return
      }

      this.variantSelector = false
    },

    /**
     * Get current variant on page load or product update.
     */
    getCurrentVariant(urlDefault) {
      if (!this.product || !this.product?.variants?.length) {
        return
      }

      const sizeVariant = this.getSelectedSizeVariant()

      if (sizeVariant) {
        this.currentVariantId = sizeVariant.id
        return
      }

      const singleAvailableVariant = this.product.variants.length === 1

      if (this.product.hasOnlyDefaultVariant || singleAvailableVariant) {
        this.currentVariantId = this.product.variants[0].id
        return
      }

      /**
       * Only pick variant if id is in URL is included on page load
       */
      if (urlDefault) {
        const variant = this.getVariantFromUrl()

        if (!variant) {
          return
        }

        this.currentVariantId = variant.id
        return
      }

      const inStockVariants = this.product.variants.filter((variant) => {
        return variant.available
      })

      const defaultVariant = inStockVariants.length
        ? inStockVariants[0]
        : this.product.variants[0]

      const variant = this.getVariantFromUrl() || defaultVariant

      this.currentVariantId = variant.id
    },

    /**
     * Get initial variant from URL parameter.
     * @returns {Object}
     */
    getVariantFromUrl() {
      const params = new URLSearchParams(document.location.search.substring(1))
      const variantId = Number(params.get('variant'))
      return this.getVariantFromId(variantId)
    },

    /**
     * Get variant object from variant ID.
     * @returns {Object}
     */
    getVariantFromId(variantId) {
      return this.product.variants.find((variant) => {
        return variant.id === variantId
      })
    },

    /**
     * Retrieves selected size variant from session storage
     * - Assigns selected variant if variant is in same related collection
     * as stored value and if variant has the same size value
     * @returns {Boolean|Object}
     */
    getSelectedSizeVariant() {
      if (!sessionStorage.getItem('selectedVariant')) {
        return false
      }

      const selectedVariant = JSON.parse(sessionStorage.getItem('selectedVariant'))

      if (this.relatedCollection.handle !== selectedVariant.relatedCollection) {
        return false
      }

      /**
       * Check if current variant has the same value as selected variant size
       * and also is not the same variant as the stored variant
       */
      const sizeVariant = this.product.variants?.find(variant => variant.selectedOptions.find(
        option => option.name?.toLowerCase() === 'size' &&
          option.value === selectedVariant.size &&
          variant.id !== selectedVariant.id,
      ))

      if (!sizeVariant) {
        return false
      }

      return sizeVariant
    },

    /**
     * Get variant title.
     * @param {Object} variant - Variant object.
     * @returns {String}
     */
    getVariantTitle(variant) {
      if (!variant) {
        return this.$string('accessibility.loading')
      }

      const sizeVariant = variant.selectedOptions?.find(
        (option) => option.name?.toLowerCase() === 'size',
      )
      const amountVariant = variant.selectedOptions?.find(
        (option) => option.name?.toLowerCase() === 'value',
      )

      let variantTitle

      if (sizeVariant) {
        variantTitle = sizeVariant.value
      } else if (amountVariant) {
        variantTitle = amountVariant.value
      } else {
        variantTitle = variant.title
      }

      return variant.available
        ? variantTitle
        : `${variantTitle} <span class="visually-hidden">${this.$string(
          'product.form.sold_out',
        )}</span>`
    },

    /**
     * Handle quantity selector update.
     * @param {String} quantity - New quantity.
     */
    handleQuantityUpdate(quantity) {
      this.quantity = quantity
    },

    /**
     * Stores selected variant in session storage
     * @param {Object} variant - Selected variant.
     */
    storeSelectedSize(variant) {
      if (!this.relatedCollection?.handle) {
        return
      }

      const sizeVariant = variant.selectedOptions.find(
        option => option.name.toLowerCase() === 'size',
      )

      const variantObject = {
        relatedCollection: this.relatedCollection?.handle,
        size: sizeVariant?.value,
        id: variant.id,
      }

      sessionStorage.setItem('selectedVariant', JSON.stringify(variantObject))
    },

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart() {
      if (!this.variant) {
        this.state = 'empty'
        return
      }

      if (!this.variant.available) {
        return
      }

      clearTimeout(this.timeout)
      this.error = ''
      this.state = 'adding'

      if (cnvs.storybook) {
        this.timeout = setTimeout(() => {
          this.state = 'added'
        }, this.$timing('slow'))

        this.timeout = setTimeout(() => {
          this.state = 'ready'
        }, this.$timing('message'))

        return
      }

      const items = [
        {
          id: this.variant.id,
          properties: this.lineItemProperties,
          quantity: this.quantity,
        },
      ]

      try {
        await this.addItem(items)
        this.addedVariant = this.variant
        this.state = 'added'

        if (
          cnvs.settings.autoOpenCartDrawer &&
          this.getComponentExists('cart-drawer')
        ) {
          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          })
        }

        await this.resetState()
      } catch (error) {
        this.state = 'error'

        if (error?.description) {
          this.error = error.description
          return
        }

        cnvs.ReportError('Failed to add product to cart', error)
      }
    },

    /**
     * Reset product form state after adding to cart after message delay.
     */
    async resetState() {
      await new Promise((resolve) => {
        setTimeout(resolve, this.$timing('message'))
      })

      this.state = 'ready'

      /**
       * If user has already changed variant then don't reset form.
       */
      if (this.variant !== this.addedVariant) {
        this.addedVariant = false
        return
      }

      this.quantity = 1

      /**
       * Revert to first available variant.
       * - If there isn't one (product out of stock) then select first variant.
       */
      let firstAvailableVariant = this.product.variants.find((variant) => {
        return variant.available
      })

      if (!firstAvailableVariant) {
        firstAvailableVariant = this.product.variants[0]
      }

      this.currentVariantId = firstAvailableVariant.id
      this.addedVariant = false
    },
  },
}
</script>

<style lang="scss">
@import "./product-form";
</style>
